import dayjs from 'dayjs';
import _ from 'lodash';
import React, { useContext, useState } from 'react';
import { useIntl } from 'react-intl';
import CircularProgress from '@material-ui/core/CircularProgress';
import {
  DetailContext,
  DetailHandle,
  PrimaryDetailActionbarButton,
  SnackbarContext,
  SnackbarVariant,
} from '@eas/common-web';
import { WorkOrder } from '@models';
import { EvidenceApiUrl } from '@enums';

// const errorsMap: Record<string, string> = {
//   INVALID_WORK_ORDER_FOR_RESYNC:
//     'Pracovný príkaz nie je možné zosynchronizovať (INVALID_WORK_ORDER_FOR_RESYNC).',
//   WORK_ORDER_RESYNC_FAILED: 'Synchronizácia zlyhala.',
//   WORK_ORDER_ALREADY_SYNCING: 'Pracovný príkaz sa už synchronizuje.',
//   DEFAULT: 'Pracovný príkaz sa nepodarilo zosynchronizovať, neznáma chyba.',
// };

export const canMuzaResync = (
  w?: WorkOrder | null,
  checkHistoryStates = true
): boolean => {
  if (!w || !w.id) {
    return false;
  }

  const isValidState = ['NAP', 'NOV'].includes(w?.currentState?.code ?? '');

  if (
    !isValidState ||
    !w.muzaSynchronized ||
    !w.toOracleSynchronized ||
    w.wasVYK
  ) {
    return false;
  }

  // keep this history states check, wasVYK is only in `list` and  `list/calendar` response, not in detail
  if (checkHistoryStates) {
    const wasVykInPast = (w?.historyStates ?? []).some(
      (h) => h?.state?.code === 'VYK'
    );

    if (wasVykInPast) {
      return false;
    }
  }

  return true;
};

export async function handleSubmitResync({
  id,
  showSnackbar,
}: {
  id: string;
  showSnackbar: (message: string, variant: SnackbarVariant) => void;
}) {
  try {
    const response = await fetch(`${EvidenceApiUrl.WORK_ORDERS}/${id}/resync`, {
      method: 'POST',
    });

    if (response.ok) {
      showSnackbar(
        'Synchronizácia PP bola spustená. Po dokončení budete informovaný notifikáciou.',
        SnackbarVariant.SUCCESS
      );

      return true;
    }

    const json = await response.json();

    const code = _.get(json, 'code');

    showSnackbar(
      `Synchronizáciu PP nebolo možné spustiť  (${code})`,
      SnackbarVariant.ERROR
    );
    return false;
  } catch (error) {
    const code = _.get(error, 'code');
    showSnackbar(
      `Synchronizáciu PP nebolo možné spustiť  (${code})`,
      SnackbarVariant.ERROR
    );
    return false;
  }
}

export async function handleCestaResync({
  id,
  showSnackbar,
}: {
  id: string;
  showSnackbar: (message: string, variant: SnackbarVariant) => void;
}) {
  try {
    const response = await fetch(
      `${EvidenceApiUrl.WORK_ORDERS}/${id}/road/resync`,
      {
        method: 'POST',
      }
    );

    if (response.ok) {
      showSnackbar(
        'Synchronizácia cesty bola dokončená',
        SnackbarVariant.SUCCESS
      );

      return true;
    }

    const json = await response.json();

    const code = _.get(json, 'code');

    showSnackbar(
      `Synchronizáciu PP nebolo možné spustiť  (${code})`,
      SnackbarVariant.ERROR
    );
    return false;
  } catch (error) {
    const code = _.get(error, 'code');
    showSnackbar(
      `Synchronizáciu PP nebolo možné spustiť  (${code})`,
      SnackbarVariant.ERROR
    );
    return false;
  }
}

export function ActionBar() {
  const intl = useIntl();

  const { isExisting, source } =
    useContext<DetailHandle<WorkOrder>>(DetailContext);

  const [loading, setLoading] = useState(false);

  const { showSnackbar } = useContext(SnackbarContext);

  if (!isExisting) {
    return null;
  }

  const muzaResyncEnabled = canMuzaResync(source?.data);
  const cestaResyncEnabled =
    (source?.data?.operationNumber ?? 0) > 10 &&
    source.data?.workOrderType?.name?.startsWith?.('Z');

  const d = source?.data?.lastSuccessfulSync;

  const lastResync = d ? dayjs(d).format('DD. MM. YYYY | HH:mm:ss') : '--';

  return (
    <>
      {source?.data?.muzaWorkOrder ? (
        <div style={{ alignItems: 'center', display: 'flex' }}>
          <PrimaryDetailActionbarButton
            label={intl.formatMessage({
              id: 'ZSD__WORK_ORDERS__DETAIL__ACTIONBAR__BUTTON__MUZA_SYNC',
              defaultMessage: 'Synchronizovať ',
            })}
            onClick={async () => {
              setLoading(true);
              await handleSubmitResync({
                id: source?.data?.id ?? '',
                showSnackbar,
              });
              setLoading(false);
            }}
            endIcon={
              loading && <CircularProgress size="20px" color="inherit" />
            }
            disabled={!muzaResyncEnabled || loading}
          />
          <span style={{ marginLeft: 16 }}>
            Posledná úspešná synchronizácia: <b>{lastResync ?? ''}</b>
          </span>
        </div>
      ) : (
        <div style={{ alignItems: 'center', display: 'flex' }}>
          <PrimaryDetailActionbarButton
            label={intl.formatMessage({
              id: 'ZSD__WORK_ORDERS__DETAIL__ACTIONBAR__BUTTON__CESTA_RESYNC',
              defaultMessage: 'Cesta resync ',
            })}
            onClick={async () => {
              setLoading(true);
              await handleCestaResync({
                id: source?.data?.id ?? '',
                showSnackbar,
              });
              setLoading(false);
              source.refresh();
            }}
            endIcon={
              loading && <CircularProgress size="20px" color="inherit" />
            }
            disabled={loading || !cestaResyncEnabled}
          />
        </div>
      )}
    </>
  );
}
